import type {
  FormattedPromotions,
  PromotionsResponse
} from '@library/types/promotions';
import type { Included, Projection } from '@library/types/projection';
import { formatToSnakeCase } from '@bo-utils/common';

export const getPromotionValues = ({
  included,
  projection
}: {
  included: Array<Included<string>>;
  projection: Projection<string>;
}): {
  discountPercentage: string;
  flashSaleLineScore: number;
  isPromo: boolean;
  optIn: boolean;
} => {
  const promotion = included.find(
    (include) =>
      include.type === 'promotion' &&
      Array.isArray(projection.relationships.usable_promotions.data) &&
      projection.relationships.usable_promotions.data.length > 0 &&
      projection?.relationships?.usable_promotions.data?.[0].id === include.id
  );

  const {
    attributes: {
      discounted_line_score: promotionFlashSaleLineScore,
      discounted_line_score_percentage: promotionDiscountPercentage,
      opt_in: optIn
    }
  } = promotion ?? { attributes: { opt_in: false } };

  const {
    attributes: {
      discount_percentage: projectionDiscountPercentage,
      flash_sale_line_score: projectionFlashSaleLineScore,
      is_promo: isPromo
    }
  } = projection;

  const discountPercentage =
    typeof promotionDiscountPercentage === 'number'
      ? String(promotionDiscountPercentage)
      : projectionDiscountPercentage;

  const flashSaleLineScore =
    promotionFlashSaleLineScore ?? projectionFlashSaleLineScore;

  return {
    discountPercentage,
    flashSaleLineScore: Number(flashSaleLineScore),
    isPromo: isPromo || !!promotion,
    optIn: optIn ?? false
  };
};

export const transformPromotions = (
  response: PromotionsResponse | undefined
): FormattedPromotions | undefined => {
  if (!response) {
    return undefined;
  }

  const formattedData = response.data.map((promotion) => {
    let userCount = 0;
    let approvedAt: string | undefined;
    let approvedBy: string | undefined;
    let createdBy: string = 'Unknown User';
    let microPromoTag: string = '';
    let macroPromoTag: string = '';

    // Safely access relationships data
    const userListRelationship =
      promotion.relationships?.promotion_eligible_user_list?.data;

    /** Extract userCount, approvedAt, and approvedBy */
    if (userListRelationship && Array.isArray(response.included)) {
      const userListId = userListRelationship.id;

      // Find matching user list in included array
      const userList = response.included.find(
        (item) =>
          item.id === userListId && item.type === 'promotion_eligible_user_list'
      );

      // Now TypeScript knows userList is specifically an IncludedUserList
      if (userList) {
        if (typeof userList.attributes.user_count === 'number') {
          userCount = userList.attributes.user_count;
        }

        // Extract approval date
        approvedAt = userList.attributes.approved_at || undefined;

        // Check for approved_by relationship
        const approvedByRelationship =
          userList.relationships?.approved_by?.data;

        if (approvedByRelationship && approvedByRelationship.id) {
          // Find admin user in included array with type guard
          const adminUser = response.included.find(
            (item) =>
              item.id === approvedByRelationship.id &&
              item.type === 'admin_user'
          );

          // Now TypeScript knows adminUser is specifically an AdminUser
          if (adminUser) {
            const firstName = adminUser.attributes.first_name || '';
            const lastName = adminUser.attributes.last_name || '';
            const fullName = `${firstName} ${lastName}`.trim();

            // If name is empty, fall back to email
            if (fullName === '') {
              approvedBy = adminUser.attributes.email || 'Unknown User';
            } else {
              approvedBy = fullName;
            }
          }
        }
      }
    }

    const adminUserRelationship = promotion.relationships?.admin_user?.data;

    if (adminUserRelationship && Array.isArray(response.included)) {
      const adminUserId = adminUserRelationship.id;

      // Find matching admin user in included array
      const adminUser = response.included.find(
        (item) => item.id === adminUserId && item.type === 'admin_user'
      );

      if (adminUser) {
        const firstName = adminUser.attributes.first_name || '';
        const lastName = adminUser.attributes.last_name || '';
        createdBy = `${firstName} ${lastName}`.trim();

        // If name is empty, fall back to email
        if (createdBy === '') {
          createdBy = adminUser.attributes.email || 'Unknown User';
        }
      }
    }

    const microPromoTagRelationship =
      promotion.relationships?.micro_promo_tag.data;
    const macroPromoTagRelationship =
      promotion.relationships?.macro_promo_tag.data;

    if (microPromoTagRelationship && Array.isArray(response.included)) {
      const microPromoTagId = microPromoTagRelationship.id;

      // Find matching micro promo tag in included array
      const microPromoTagItem = response.included.find(
        (item) => item.id === microPromoTagId && item.type === 'micro_promo_tag'
      );

      if (microPromoTagItem) {
        microPromoTag = microPromoTagItem.attributes.name;
      }
    }

    if (macroPromoTagRelationship && Array.isArray(response.included)) {
      const macroPromoTagId = macroPromoTagRelationship.id;

      // Find matching macro promo tag in included array
      const macroPromoTagItem = response.included.find(
        (item) => item.id === macroPromoTagId && item.type === 'macro_promo_tag'
      );

      if (macroPromoTagItem) {
        macroPromoTag = macroPromoTagItem.attributes.name;
      }
    }

    return {
      ...promotion,
      attributes: {
        ...promotion.attributes,
        allowedLeagueIds: promotion.attributes.allowed_league_ids,
        fundType: promotion.attributes.fund_type,
        payoutType: promotion.attributes.payout_type,
        entryType: promotion.attributes.entry_type,
        playerCount: promotion.attributes.player_count,
        optInBy: promotion.attributes.opt_in_by,
        useBy: promotion.attributes.use_by,
        shortDescription: promotion.attributes.short_description,
        longDescription: promotion.attributes.long_description,
        payoutCents: promotion.attributes.payout_cents,
        maxCashBackAmountCents: promotion.attributes.max_cash_back_amount_cents,
        gameStartDate: promotion.attributes.game_start_date,
        correlatedPayoutsAllowed:
          promotion.attributes.correlated_payouts_allowed,
        createdAt: promotion.attributes.created_at,
        updatedAt: promotion.attributes.updated_at,
        payoutMultipliers: promotion.attributes.payout_multipliers,
        optedInDate: promotion.attributes.opted_in_date,
        entrySlipDescription: promotion.attributes.entry_slip_description,
        optIn: promotion.attributes.opt_in,
        availableAt: promotion.attributes.visible_to_users_at,
        discountedLineScore: promotion.attributes.discounted_line_score,
        projectionId: promotion.attributes.projection_id,
        entryAmountCents: promotion.attributes.entry_amount_cents,
        customHeadshot: promotion.attributes.custom_headshot,
        ctaLink: promotion.attributes.cta_link,
        ctaText: promotion.attributes.cta_text,
        maxMatchAmountCents: promotion.attributes.max_match_amount_cents,
        matchAmountPercentage: promotion.attributes.match_amount_percentage,
        allLiveSquares: promotion.attributes.all_live_squares,
        atLeastOneLiveSquare: promotion.attributes.at_least_one_live_square,
        allDemons: promotion.attributes.all_demons,
        atLeastOneDemon: promotion.attributes.at_least_one_demon,
        allGoblins: promotion.attributes.all_goblins,
        atLeastOneGoblin: promotion.attributes.at_least_one_goblin,
        eligibleUserListCount: userCount, // set the user count from included array
        maxDollarValue: promotion.attributes.max_dollar_value,
        discountedLineScorePercentage:
          promotion.attributes.discounted_line_score_percentage,
        amountRemainingCents: promotion.attributes.amount_remaining_cents,
        approvedAt,
        approvedBy,
        createdBy,
        microPromoTag,
        macroPromoTag,
        reimbursementType: promotion.attributes.reimbursement_type
      }
    };
  });

  return { data: formattedData, meta: response.meta };
};

export const getPromotionAdminLink = (id: string, type: string) => {
  const { hostname, protocol } = window.location;
  const apiHostname = ['api', ...hostname.split('.').slice(1)].join('.');
  let baseUrl = `${protocol}//${apiHostname}/radmin`;

  if (hostname.includes('localhost')) {
    baseUrl = 'http://localhost/radmin';
  }

  const promoType = formatToSnakeCase(type).replace(/^_+/, '');
  const url = `${baseUrl}/${promoType}/${id}`;

  return url;
};

export const formatLabelByCampaignType = (str: string): string => {
  switch (str) {
    case 'OneTime':
      return 'One-Time';
    case 'Recurring':
      return 'Recurring';
    case 'DynamicTrigger':
      return 'Dynamic Trigger';
    default:
      return '';
  }
};

export const formatLabelByPromotionType = (str: string): string => {
  if (str === 'FreeEntryPromotion') {
    return 'Free Lineup';
  }

  return str
    .replace(/([a-z\d])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .trim();
};
